import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  RefreshCw,
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  CreditCard,
  DollarSign,
  ListChecks,
  Loader2,
  Filter,
  TrendingUp,
} from "lucide-react";
import {
  getVendorPayoutSummary,
  getVendorAccountingStatement,
} from "../../services/vendorPayoutService";
import { formatDateTime } from "../../utils/dateUtils";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";
import DatePicker from "../../components/client/DatePicker";
import { useVendorStripeConnect } from "../../services/vendorTaxProfileService";
import StripeConnectStatusBadge from "../../components/shared/StripeConnectStatusBadge";
import { getStripeConnectStatus } from "../../utils/stripeConnectStatus";

const fmtMoney = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return "$0.00";
  return `$${v.toFixed(2)}`;
};

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "ytd", label: "Year to date" },
  { id: "custom", label: "Custom" },
];

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getPresetRange = (presetId) => {
  const today = new Date();
  const end = toIsoDate(today);
  if (presetId === "30d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { date_from: toIsoDate(start), date_to: end };
  }
  if (presetId === "90d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 89);
    return { date_from: toIsoDate(start), date_to: end };
  }
  if (presetId === "ytd") {
    return { date_from: `${today.getFullYear()}-01-01`, date_to: end };
  }
  return { date_from: "", date_to: "" };
};

const VendorAccounting = () => {
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const summaryQuery = useQuery({
    queryKey: ["vendor-accounting-summary"],
    queryFn: async () => getVendorPayoutSummary(),
  });

  const statementParams = useMemo(
    () => ({
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      limit: 250,
    }),
    [dateFrom, dateTo],
  );

  const statementQuery = useQuery({
    queryKey: ["vendor-accounting-statement", statementParams],
    queryFn: async () => getVendorAccountingStatement(statementParams),
  });

  const { data: connectRes, isLoading: connectLoading } =
    useVendorStripeConnect();
  const connect = connectRes?.data;
  const connectStatus = getStripeConnectStatus(connect);

  const summary = summaryQuery.data?.data || statementQuery.data?.data?.summary;
  const entries = statementQuery.data?.data?.entries ?? [];

  const applyPreset = (presetId) => {
    setDatePreset(presetId);
    if (presetId === "custom") return;
    const range = getPresetRange(presetId);
    setDateFrom(range.date_from);
    setDateTo(range.date_to);
  };

  const refreshing = summaryQuery.isFetching || statementQuery.isFetching;
  const earned = Number(summary?.total_earned_from_orders || 0);
  const disbursed = Number(summary?.total_disbursed || 0);
  const available = Number(summary?.available_balance || 0);
  const settledPct =
    earned > 0
      ? Math.min(100, Math.round((disbursed / earned) * 1000) / 10)
      : 100;

  return (
    <div className="space-y-5 max-w-6xl mx-auto pb-12">
      <VendorPageHeader
        title="Accounting"
        subtitle="Internal marketplace only — order earnings in, payouts out, and the balance still owed to you. External / private jobs are not included."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/vendor/earnings"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm whitespace-nowrap"
            >
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              Earnings
            </Link>
            <Link
              to="/vendor/payments"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm whitespace-nowrap"
            >
              <CreditCard className="w-4 h-4 text-ink" />
              Payments
            </Link>
            <Link
              to="/vendor/payout-disbursements"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm whitespace-nowrap"
            >
              <Wallet className="w-4 h-4 text-amber-600" />
              Payouts
            </Link>
            <button
              type="button"
              onClick={() => {
                summaryQuery.refetch();
                statementQuery.refetch();
              }}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm disabled:opacity-50 whitespace-nowrap"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.accounting.title}
        steps={VENDOR_PAGE_GUIDES.accounting.steps}
        icon={ListChecks}
      />

      <div className="rounded-lg border border-border bg-surface-muted px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink">
            Stripe Connect
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {connectLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-ink-secondary" />
            ) : (
              <StripeConnectStatusBadge connect={connect} />
            )}
            {connect?.account_id ? (
              <span className="font-mono text-[11px] text-ink-secondary truncate">
                {connect.account_id}
              </span>
            ) : (
              <span className="text-sm text-ink-secondary">
                Link Express to receive automatic payouts.
              </span>
            )}
          </div>
        </div>
        <Link
          to="/vendor/tax-profile"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink hover:bg-surface-muted whitespace-nowrap"
        >
          <CreditCard className="w-4 h-4" />
          {connectStatus.payoutReady
            ? "Manage Connect"
            : connectStatus.accountId
              ? "Continue Connect"
              : "Set up Connect"}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink">
              Earned from orders
            </span>
            <div className="p-2 rounded-xl bg-surface-muted text-ink">
              <ArrowUpRight className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
            {summaryQuery.isLoading ? "…" : fmtMoney(earned)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            After platform fees · lifetime
          </p>
        </div>

        <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Payouts received
            </span>
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700">
              <ArrowDownLeft className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
            {summaryQuery.isLoading ? "…" : fmtMoney(disbursed)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Money already sent to you
          </p>
        </div>

        <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
              Available balance
            </span>
            <div className="p-2 rounded-xl bg-amber-50 text-amber-700">
              <Wallet className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
            {summaryQuery.isLoading ? "…" : fmtMoney(available)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Still owed by the platform
          </p>
        </div>

        <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-secondary">
              Settled
            </span>
            <div className="p-2 rounded-xl bg-gray-100 text-ink-secondary">
              <BookOpen className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
            {summaryQuery.isLoading ? "…" : `${settledPct}%`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Share of earnings already paid out
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-gradient-to-r from-surface-muted/80 via-white to-emerald-50/80 px-4 py-3 text-sm text-ink-secondary">
        <span className="font-medium text-ink">Read your books: </span>
        When a client pays for a platform booking, your net share (after fee) is
        a credit. When vendaList sends a payout, it appears as a debit. Private
        bookings you manage yourself are not included here.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link
          to="/vendor/earnings"
          className="rounded-lg border border-border bg-surface p-4 hover:border-border transition-colors shadow-sm"
        >
          <DollarSign className="w-5 h-5 text-ink mb-2" />
          <p className="text-sm font-semibold text-ink">Earnings overview</p>
          <p className="text-xs text-gray-500 mt-1">
            Charts and totals for marketplace income
          </p>
        </Link>
        <Link
          to="/vendor/payments"
          className="rounded-lg border border-border bg-surface p-4 hover:border-border transition-colors shadow-sm"
        >
          <CreditCard className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="text-sm font-semibold text-ink">Payment receipts</p>
          <p className="text-xs text-gray-500 mt-1">
            Each client payment tied to a booking
          </p>
        </Link>
        <Link
          to="/vendor/payout-disbursements"
          className="rounded-lg border border-border bg-surface p-4 hover:border-amber-300 transition-colors shadow-sm"
        >
          <Wallet className="w-5 h-5 text-amber-600 mb-2" />
          <p className="text-sm font-semibold text-ink">Payout history</p>
          <p className="text-xs text-gray-500 mt-1">
            Export or print disbursements received
          </p>
        </Link>
      </div>

      <div className="bg-surface rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink">
                Ledger statement
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Chronological credits and debits with running balance
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap p-1 bg-gray-100 rounded-xl border border-border">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all ${datePreset === preset.id ? "bg-surface text-ink shadow-sm" : "text-ink-secondary"}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {datePreset === "custom" && (
              <div className="flex flex-wrap items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-gray-400" />
                <div className="w-[150px]">
                  <DatePicker
                    value={dateFrom}
                    onChange={(v) => {
                      setDateFrom(v);
                      setDatePreset("custom");
                    }}
                    placeholder="From"
                    size="sm"
                  />
                </div>
                <div className="w-[150px]">
                  <DatePicker
                    value={dateTo}
                    onChange={(v) => {
                      setDateTo(v);
                      setDatePreset("custom");
                    }}
                    placeholder="To"
                    size="sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {statementQuery.isLoading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-7 h-7 animate-spin text-ink mx-auto" />
          </div>
        ) : statementQuery.error ? (
          <div className="p-8 text-center text-sm text-red-600">
            {statementQuery.error.message || "Could not load your statement."}
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            No ledger activity for this period yet. Completed platform bookings
            will appear as credits here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-left text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">What happened</th>
                  <th className="px-4 py-3 font-semibold">Reference</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Money in
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Money out
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((row) => {
                  const isCredit = row.type === "credit";
                  return (
                    <tr key={row.id} className="hover:bg-surface-muted/80">
                      <td className="px-4 py-3 whitespace-nowrap text-ink-secondary text-xs">
                        {row.occurred_at
                          ? formatDateTime(row.occurred_at)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 min-w-[200px]">
                        <div className="flex items-start gap-2">
                          <span
                            className={`mt-0.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${isCredit ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}
                          >
                            {isCredit ? "Earned" : "Payout"}
                          </span>
                          <div>
                            <p className="font-medium text-ink">
                              {isCredit ? "Order earnings" : "Payout received"}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {row.description || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                        {row.reference || row.booking_reference || "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-emerald-700">
                        {isCredit ? fmtMoney(row.amount) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-amber-700">
                        {!isCredit ? fmtMoney(row.amount) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-ink">
                        {fmtMoney(row.running_balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorAccounting;
